import { Badge } from "@/components/ui/badge";

import type { Pro014StoresPreview as Pro014StoresPreviewData } from "../../types";
import { PRO014_STORES_TEXTS } from "./constants";

type PreviewTone = "chosen" | "add" | "reopen" | "end";

const TONE_CLASS: Record<PreviewTone, string> = {
  chosen: "bg-secondary text-secondary-foreground hover:bg-secondary",
  add: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100",
  reopen: "bg-sky-100 text-sky-800 hover:bg-sky-100",
  end: "bg-red-100 text-red-800 hover:bg-red-100",
};

interface PreviewLineProps {
  label: string;
  storeCodes: number[];
  tone: PreviewTone;
}

function PreviewLine({ label, storeCodes, tone }: PreviewLineProps): JSX.Element | null {
  if (storeCodes.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 text-xs" data-testid={`stores-preview-${tone}`}>
      <Badge className={TONE_CLASS[tone]}>{label}</Badge>
      {storeCodes.map((storeCode: number) => (
        <Badge key={storeCode} variant="outline" className="font-mono text-xs">
          {storeCode}
        </Badge>
      ))}
    </div>
  );
}

interface Pro014StoresPreviewProps {
  preview: Pro014StoresPreviewData;
}

export function Pro014StoresPreview({ preview }: Pro014StoresPreviewProps): JSX.Element | null {
  if (!preview.hasChanges) return null;

  return (
    <div className="space-y-2 rounded-md border bg-muted/30 p-3" aria-live="polite">
      <p className="text-xs font-medium">{PRO014_STORES_TEXTS.PREVIEW_TITLE}</p>
      {preview.switchesToAll && <p className="text-xs">{PRO014_STORES_TEXTS.SWITCH_TO_ALL}</p>}
      {preview.switchesToSpecific && (
        <p className="text-xs">{PRO014_STORES_TEXTS.SWITCH_TO_SPECIFIC}</p>
      )}
      <PreviewLine label={PRO014_STORES_TEXTS.CHOSEN} storeCodes={preview.chosen} tone="chosen" />
      <PreviewLine label={PRO014_STORES_TEXTS.ADD} storeCodes={preview.toAdd} tone="add" />
      <PreviewLine label={PRO014_STORES_TEXTS.REOPEN} storeCodes={preview.toReopen} tone="reopen" />
      <PreviewLine label={PRO014_STORES_TEXTS.END} storeCodes={preview.toEnd} tone="end" />
    </div>
  );
}
